import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import CategoryButtons from './CategoryButtons'
import { DropPositions } from '../types'

export default function CategoryItem({
  category,
  isActive,
  readOnly,
  onClick,
  onChangePosition,
  onEdit,
  onRemove
}: {
  category: GraphQL.CategoryFragmentFragment
  isActive: boolean
  readOnly: boolean
  onClick: () => void
  onChangePosition: (position: number) => void
  onEdit: (id: number) => void
  onRemove: (id: number) => void
}) {
  const elementRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  const [dragging, setDragging] = useState(false)
  const [draggedOver, setDraggedOver] = useState(false)
  const [overPosition, setOverPosition] = useState<DropPositions | null>(null)

  const cover = useMemo(() => {
    return getFragment(SharedAPI.file.FILE_FRAGMENT, category.cover)
  }, [category])

  useEffect(() => {
    if (!elementRef.current) return
    if (!handleRef.current) return

    return draggable({
      element: elementRef.current,
      dragHandle: handleRef.current,
      getInitialDataForExternal: () => ({ 'text/plain': category.name }),
      onDragStart: () => setDragging(true),
      onDrop({ location }) {
        setDragging(false)

        const destination = location.current.dropTargets[0]

        if (!destination) return

        const position = destination.data.position as number
        const overPosition = destination.data.overPosition as DropPositions

        if (overPosition === DropPositions.top) {
          if (position > category.position) onChangePosition(position - 1)
          else onChangePosition(position)
        }

        if (overPosition === DropPositions.bottom) {
          if (position > category.position) onChangePosition(position)
          else onChangePosition(position + 1)
        }
      }
    })
  }, [category.name, category.position, setDragging, onChangePosition])

  useEffect(() => {
    if (!elementRef.current) return

    return dropTargetForElements({
      element: elementRef.current,
      onDragEnter: () => setDraggedOver(true),
      onDragLeave: () => setDraggedOver(false),
      onDrag({ location }) {
        const mouseY = location.current.input.clientY
        const elementRect = elementRef.current?.getBoundingClientRect()

        if (!elementRect) return

        const elementMiddle = elementRect.top + elementRect.height / 2

        setOverPosition(
          mouseY < elementMiddle ? DropPositions.top : DropPositions.bottom
        )
      },
      onDrop: () => setDraggedOver(false),
      getData: () => ({ position: category.position, overPosition })
    })
  }, [category.position, setDraggedOver, overPosition])

  return (
    <article ref={elementRef} className="relative flex flex-col">
      <div
        className={clsx(
          '-mt-px h-[2px] w-full rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.top && 'opacity-100'
        )}
      />
      <div className={clsx('my-2 flex flex-row', dragging && 'opacity-50')}>
        {!readOnly && (
          <CategoryButtons
            category={category}
            dragHandleRef={handleRef}
            onEdit={() => onEdit(category.id)}
            onRemove={() => onRemove(category.id)}
            onUp={() => onChangePosition(category.position - 1)}
            onDown={() => onChangePosition(category.position + 1)}
          />
        )}
        <button
          className={clsx(
            'relative flex flex-1 overflow-hidden rounded',
            isActive && 'outline-3 outline-solid outline-primary'
          )}
          tabIndex={0}
          onClick={onClick}
        >
          {cover && <img src={cover?.remoteUrl} className="size-full" />}
          <h3 className="bg-glass absolute bottom-0 left-0 rounded-tr px-5 py-3 text-heading4 text-text">
            {category.name}
          </h3>
        </button>
      </div>
      <div
        className={clsx(
          '-mb-px h-[2px] w-full rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.bottom && 'opacity-100'
        )}
      />
    </article>
  )
}
